import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Search, RefreshCw, XCircle, Eye, X, Filter,
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2,
  Clock, RotateCcw, Ban, FileText
} from 'lucide-react';
import '../pages/Cobrancas.css';
import { apiFetch } from '../services/api';

const STATUS_MAP = {
  1: { label: 'Cancelada',      cls: 'cob-status-cancelled', Icon: Ban },
  2: { label: 'Aguardando',     cls: 'cob-status-pending',    Icon: Clock },
  3: { label: 'Irregular',      cls: 'cob-status-overdue',    Icon: AlertTriangle },
  4: { label: 'Faturado',       cls: 'cob-status-pending',    Icon: FileText },
  5: { label: '✅ PAGO',        cls: 'cob-status-received',   Icon: CheckCircle2 },
  6: { label: 'Estornado',      cls: 'cob-status-cancelled',  Icon: RotateCcw },
};

const statusFiltroOptions = [
  { value: '',  label: 'Todos os status' },
  { value: '2', label: 'Aguardando' },
  { value: '3', label: 'Irregular' },
  { value: '4', label: 'Faturado' },
  { value: '5', label: '✅ PAGO' },
  { value: '1', label: 'Cancelada' },
  { value: '6', label: 'Estornado' },
];

const StatusBadge = ({ status }) => {
  const info = STATUS_MAP[status] || { label: 'Desconhecido', cls: 'cob-status-pending', Icon: Clock };
  const { label, cls, Icon } = info;
  return (
    <span className={`cob-status-badge ${cls}`}>
      <Icon size={12} /> {label}
    </span>
  );
};

const formatarData = (dataStr) => {
  if (!dataStr) return '—';
  if (/^\d{8}$/.test(dataStr)) {
    return `${dataStr.slice(0, 2)}/${dataStr.slice(2, 4)}/${dataStr.slice(4)}`;
  }
  const [ano, mes, dia] = String(dataStr).slice(0, 10).split('-');
  if (!ano || !mes || !dia) return dataStr;
  return `${dia}/${mes}/${ano}`;
};

const formatarValor = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PAGE_SIZE = 20;

export default function CobrancasCpfl() {
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [linhaCarregando, setLinhaCarregando] = useState(null);
  const [detalheCobranca, setDetalheCobranca] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (search) params.append('search', search);
      if (statusFiltro) params.append('status', statusFiltro);

      const r = await apiFetch(`/api/cpfl/cobrancas?${params.toString()}`);
      setCobrancas(r.data || []);
      setTotal(r.total || 0);
      setTotalPages(r.totalPages || 1);
    } catch (err) {
      console.error('Erro ao carregar cobranças CPFL:', err);
      setError('Não foi possível carregar as cobranças CPFL.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFiltro]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleBuscar = () => {
    setPage(1);
    setSearch(/^[\d.\-\/\s]+$/.test(searchInput.trim()) ? searchInput.trim().replace(/\D/g, '') : searchInput.trim());
  };

  const handleSincronizarRetornos = async () => {
    setSincronizando(true);
    try {
      const r = await apiFetch('/api/cpfl/cobrancas/sincronizar-retornos', { method: 'POST' });
      alert(r.message || 'Retornos sincronizados com sucesso.');
      await carregar();
    } catch (err) {
      console.error('Erro ao sincronizar retornos CPFL:', err);
      alert('Erro ao sincronizar retornos da CPFL. Tente novamente.');
    } finally {
      setSincronizando(false);
    }
  };

  const handleAtualizarStatus = async (cobranca) => {
    setLinhaCarregando(cobranca.id);
    try {
      const r = await apiFetch(`/api/cpfl/cobrancas/${cobranca.id}/atualizar-status`, { method: 'POST' });
      setCobrancas((prev) => prev.map((c) => (c.id === cobranca.id ? { ...c, ...r.cobranca } : c)));
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      alert('Erro ao consultar status na CPFL.');
    } finally {
      setLinhaCarregando(null);
    }
  };

  const abrirCancelamento = (cobranca) => {
    setCancelando(cobranca);
    setMotivoCancelamento('');
  };

  const confirmarCancelamento = async () => {
    if (!motivoCancelamento.trim()) {
      alert('Informe o motivo do cancelamento.');
      return;
    }
    setLinhaCarregando(cancelando.id);
    try {
      await apiFetch(`/api/cpfl/cobrancas/${cancelando.id}/cancelar`, {
        method: 'POST',
        body: JSON.stringify({ motivo: motivoCancelamento.trim() }),
      });
      setCancelando(null);
      await carregar();
    } catch (err) {
      console.error('Erro ao cancelar cobrança CPFL:', err);
      alert('Erro ao cancelar cobrança na CPFL: ' + err.message);
    } finally {
      setLinhaCarregando(null);
    }
  };

  return (
    <div className="cobrancas-page-container">
      <div className="cobrancas-page-header">
        <h1 className="cobrancas-page-title">
          <Zap size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#d97706' }} />
          Cobranças CPFL
        </h1>
        <p style={{ color: '#64748b', fontSize: 14, margin: '4px 0 0 0' }}>
          Cobranças geradas via débito em conta de energia. O status é informado pela CPFL
          somente através dos arquivos de retorno — use "Sincronizar Retornos" para atualizar.
        </p>
      </div>

      <div className="cobrancas-card">
        <div className="cobrancas-toolbar">
          <div className="cobrancas-toolbar-left">
            <div className="cobrancas-search-box">
              <input
                className="cobrancas-search-input"
                placeholder="Buscar por cliente, CPF, instalação ou identificação"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
              />
              <button className="cobrancas-search-btn" onClick={handleBuscar} title="Buscar">
                <Search size={16} />
              </button>
            </div>

            <select
              className="filter-select"
              value={statusFiltro}
              onChange={(e) => { setStatusFiltro(e.target.value); setPage(1); }}
              style={{ maxWidth: 190 }}
            >
              {statusFiltroOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="cobrancas-toolbar-right">
            <button className="cobrancas-btn-batch" onClick={handleSincronizarRetornos} disabled={sincronizando}>
              <RefreshCw size={15} className={sincronizando ? 'spinning' : ''} />
              {sincronizando ? 'Sincronizando...' : 'Sincronizar Retornos'}
            </button>
          </div>
        </div>

        {loading && (
          <div className="cobrancas-loading">
            <RefreshCw size={24} className="spinning" />
            <span>Carregando cobranças CPFL...</span>
          </div>
        )}

        {!loading && error && (
          <div className="cobrancas-error">
            <AlertTriangle size={24} />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && cobrancas.length === 0 && (
          <div className="cobrancas-empty">
            <Zap size={32} style={{ color: '#cbd5e1' }} />
            <span>Nenhuma cobrança CPFL encontrada.</span>
          </div>
        )}

        {!loading && !error && cobrancas.length > 0 && (
          <div className="cobrancas-table-wrapper">
            <table className="cobrancas-table-modern">
              <thead>
                <tr>
                  <th className="col-nome">Cliente</th>
                  <th>Instalação (UC)</th>
                  <th>PN</th>
                  <th>Produto</th>
                  <th className="col-valor">Valor</th>
                  <th>Tipo</th>
                  <th>Mês Referência</th>
                  <th>Status</th>
                  <th>Data Pagamento</th>
                  <th>Valor Arrecadado</th>
                  <th className="col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {cobrancas.map((c) => (
                  <tr key={c.id} className="cobrancas-table-row">
                    <td>
                      <div className="cliente-cell">
                        <Zap size={15} className={`cliente-avatar-icon status-${STATUS_MAP[c.status]?.cls?.replace('cob-status-', '') || 'pending'}`} />
                        <div>
                          <div className="cliente-nome">{c.clienteNome || '—'}</div>
                          <div className="descricao-text">{c.clienteCpf || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="pagamento-text">{c.instalacao || '—'}</td>
                    <td className="pagamento-text">{c.pn || '—'}</td>
                    <td className="pagamento-text">{c.produto || '—'}</td>
                    <td className="valor-text">{formatarValor(c.valor)}</td>
                    <td className="descricao-text">
                      {c.parcelaIndeterminada ? 'Recorrente' : `Parcelado (${c.qtdParcelas || 1}x)`}
                    </td>
                    <td className="vencimento-cell">
                      <span className="vencimento-data">{c.mesReferencia || formatarData(c.dataCadastro)}</span>
                    </td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className="vencimento-cell">
                      {c.status === 5 && c.dataPagamento ? (
                        <span className="vencimento-data" style={{ color: '#059669', fontWeight: 600 }}>
                          {formatarData(c.dataPagamento)}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td className="valor-text">
                      {c.status === 5 && c.valorArrecadado ? (
                        <span style={{ color: '#059669', fontWeight: 600 }}>
                          {formatarValor(c.valorArrecadado)}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div className="acoes-buttons">
                        <button className="acao-btn acao-btn-info" title="Ver detalhes" onClick={() => setDetalheCobranca(c)}>
                          <Eye size={15} />
                        </button>
                        <button
                          className="acao-btn"
                          title="Atualizar status (ConsultaParcelamento)"
                          onClick={() => handleAtualizarStatus(c)}
                          disabled={linhaCarregando === c.id}
                        >
                          <RefreshCw size={15} className={linhaCarregando === c.id ? 'spinning' : ''} />
                        </button>
                        {c.status !== 1 && c.status !== 6 && (
                          <button className="acao-btn delete" title="Cancelar cobrança" onClick={() => abrirCancelamento(c)}>
                            <XCircle size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="cobrancas-footer">
            <p className="cobrancas-footer-text">{total} cobrança(s) encontrada(s)</p>
            <div className="pagination">
              <button className="pag-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={16} />
              </button>
              <span className="pag-info">Página {page} de {totalPages}</span>
              <button className="pag-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {detalheCobranca && (
        <div className="cobrancas-filter-modal" onClick={() => setDetalheCobranca(null)}>
          <div className="cobrancas-filter-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="cobrancas-filter-header">
              <h3>Detalhes da Cobrança CPFL</h3>
              <button className="cobrancas-filter-close" onClick={() => setDetalheCobranca(null)}><X size={18} /></button>
            </div>
            <div className="cobrancas-filter-body">
              <div className="filter-periods-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {[
                  ['Cliente', detalheCobranca.clienteNome],
                  ['CPF', detalheCobranca.clienteCpf],
                  ['Instalação (UC)', detalheCobranca.instalacao],
                  ['PN', detalheCobranca.pn],
                  ['Produto', detalheCobranca.produto],
                  ['Identificação', detalheCobranca.identificacao],
                  ['Valor', formatarValor(detalheCobranca.valor)],
                  ['Tipo', detalheCobranca.parcelaIndeterminada ? 'Recorrente' : `Parcelado (${detalheCobranca.qtdParcelas || 1}x)`],
                  ['Mês Referência', detalheCobranca.mesReferencia || '—'],
                  ['Data de Vencimento', formatarData(detalheCobranca.dataVencimento)],
                  ['Data de Cadastro', formatarData(detalheCobranca.dataCadastro)],
                  ['Status', STATUS_MAP[detalheCobranca.status]?.label || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="filter-section">
                    <div className="filter-section-title">{label}</div>
                    <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 500 }}>{value || '—'}</div>
                  </div>
                ))}
              </div>
              {detalheCobranca.status === 1 && (
                <div className="filter-section" style={{ marginTop: 10 }}>
                  <div className="filter-section-title">Motivo do Cancelamento</div>
                  <div style={{ fontSize: 14, color: '#e11d48' }}>{detalheCobranca.descMotCancelamento || '—'}</div>
                </div>
              )}
            </div>
            <div className="cobrancas-filter-footer">
              <div />
              <button className="cobrancas-filter-btn-primary" onClick={() => setDetalheCobranca(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {cancelando && (
        <div className="cobrancas-filter-modal" onClick={() => setCancelando(null)}>
          <div className="cobrancas-filter-content" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="cobrancas-filter-header">
              <h3>Cancelar Cobrança CPFL</h3>
              <button className="cobrancas-filter-close" onClick={() => setCancelando(null)}><X size={18} /></button>
            </div>
            <div className="cobrancas-filter-body">
              <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>
                Cliente: <strong>{cancelando.clienteNome}</strong> · Instalação: <strong>{cancelando.instalacao}</strong>
              </p>
              <div className="filter-section">
                <div className="filter-section-title">Motivo do cancelamento *</div>
                <textarea
                  value={motivoCancelamento}
                  onChange={(e) => setMotivoCancelamento(e.target.value)}
                  rows={3}
                  maxLength={50}
                  placeholder="Ex: Solicitação do cliente"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', marginTop: 4 }}>{motivoCancelamento.length}/50 caracteres (limite da CPFL)</div>
              </div>
            </div>
            <div className="cobrancas-filter-footer">
              <button className="cobrancas-filter-btn-secondary" onClick={() => setCancelando(null)}>Voltar</button>
              <button
                className="cobrancas-filter-btn-primary"
                style={{ background: '#e11d48' }}
                onClick={confirmarCancelamento}
                disabled={linhaCarregando === cancelando.id}
              >
                {linhaCarregando === cancelando.id ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
